import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Tooltip,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useGetClientsQuery } from '../redux/services/clientApi';
import { useNavigate } from 'react-router-dom';

// Local storage keys
const STORAGE_KEY = 'clientsFilters';

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const Clients = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  
  // Get saved filters on component mount
  const initialFilters = getSavedFilters();
  
  // Pagination state with localStorage initialization
  const [page, setPage] = useState(initialFilters.page || 0);
  const [pageSize, setPageSize] = useState(() => {
    return initialFilters.pageSize || 25;
  });
  
  // Loading states
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  
  // Pagination model for DataGrid
  const [paginationModel, setPaginationModel] = useState({
    pageSize: pageSize,
    page: page,
  });
  
  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      page: page,
      pageSize: pageSize,
      search: searchQuery,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [page, pageSize, searchQuery]);
  
  // Load initial search query from saved filters
  useEffect(() => {
    if (initialFilters.search) {
      setSearchQuery(initialFilters.search);
      setActiveSearchQuery(initialFilters.search);
    }
    // eslint-disable-next-line
  }, []);

  const {
    data: clients,
    isLoading: isClientsLoading,
    error: clientsError,
    refetch,
  } = useGetClientsQuery({ search: activeSearchQuery, page: page + 1, limit: pageSize });

  // Safely extract the array of clients from the API response
  const clientsArray = useMemo(() => {
    if (!clients) return [];
    
    if (clients.clients && Array.isArray(clients.clients)) {
      return clients.clients;
    } else {
      console.warn('Unexpected clients data format:', clients);
      return [];
    }
    
  }, [clients]);

  // Handle pagination model change (page size and current page)
  const handlePaginationModelChange = async (model) => {
    setIsPaginationLoading(true);
    if (paginationModel.pageSize !== model.pageSize) {
      model.page = 0;
    }
    setPaginationModel(model);
    setPage(model.page);
    setPageSize(model.pageSize);
    
    // Save to localStorage
    const filters = {
      ...getSavedFilters(),
      pageSize: model.pageSize,
      page: model.page
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    
    try {
      await refetch();
    } finally {
      setIsPaginationLoading(false);
    }
  };

  // Handle search submit
  const handleSearchSubmit = async () => {
    setIsSearchLoading(true);
    setActiveSearchQuery(searchQuery);
    // Reset to first page when searching
    setPage(0);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    try {
      await refetch();
    } finally {
      setIsSearchLoading(false);
    }
  };
  
  // Handle pressing Enter in search field
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Handle clearing filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setPage(0);
    setPaginationModel({
      pageSize: 25,
      page: 0
    });
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleViewMessages = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const isLoading = isClientsLoading || isPaginationLoading || isSearchLoading;

  // Only show loading state if we're fetching the initial data
  if (isClientsLoading && !clients) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading clients...</Typography>
      </Box>
    );
  }

  // Add additional debugging for the error state
  if (clientsError) {
    return (
      <Typography variant="h4" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Error loading clients: {clientsError.status} {clientsError.data?.message || 'Unknown error'}
      </Typography>
    );
  }

  // Define DataGrid columns with better error handling
  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 100,
      valueGetter: (params) => {
        if (!params) return '';
        return params || 'Unknown'
      },
    },
    { 
      field: 'avatar', 
      headerName: 'Avatar',
      width: 100,
      display: 'flex',
      renderCell: (params) => {
        return <Avatar src={params.value}>
          {params.row.name.toUpperCase().substring(0, 1)}
        </Avatar>;
      }
    },
    { 
      field: 'name', 
      headerName: 'Name', 
      minWidth: 250,
      flex: 1,
      renderCell: (params) => {
        if (!params) return '<></>';
        return <>
          <span variant="body2">{params.value}</span>
          <div style={{float: 'right'}}>
            {params.row.web1*1 === 1 && <Chip label="Web1" color="primary" size="small" />}
            {params.row.web2*1 === 1 && <Chip label="Web2" color="primary" size="small" />}
            {params.row.is_order*1 === 1 && <Chip label="Order" color="primary" size="small" />}
            {params.row.is_sale*1 === 1 && <Chip label="Sale" color="primary" size="small" />}
            {params.row.is_prepay*1 === 1 && <Chip label="Prepay" color="primary" size="small" />}
          </div>
        </>;
      },
    },
    { 
      field: 'client_type', 
      headerName: 'Client type', 
      width: 120,
      valueGetter: (params) => {
        if (!params) return '';
        return params || 'Unknown'
      },
    },
    /*
    { 
      field: 'recepient', 
      headerName: 'Recepient', 
      width: 120,
      valueGetter: (params) => {
        if (!params) return '';
        return params || 'Unknown'
      },
    },
    */
    { 
      field: 'tag', 
      headerName: 'Tag', 
      width: 150,
      valueGetter: (params) => {
        if (!params) return '';
        return params || '';
      },
    },
    /*
    { 
      field: 'client_group', 
      headerName: 'Client group', 
      width: 150,
      valueGetter: (params) => {
        if (!params) return '';
        return params || '';
      },
    },
    */
    { 
      field: 'created', 
      headerName: 'Created', 
      width: 140,
      valueGetter: (params) => {
        if (!params) return '';
        return params || 'Unknown'
      },
    },
    { 
      field: 'updated', 
      headerName: 'Updated', 
      width: 140,
      valueGetter: (params) => {
        if (!params) return '';
        return params || 'Unknown'
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        return (
          <Box>
            <Tooltip title="View messages">
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleViewMessages(params.row.id)}
              >
                View messages
              </Button>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Paper elevation={1} sx={{ width: '100%', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Clients
        </Typography>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          sx={{ flex: 1, maxWidth: 500 }}
          variant="outlined"
          size="small"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          sx={{ ml: 1 }}
          variant="contained"
          color="primary"
          onClick={handleSearchSubmit}
          disabled={isLoading}
        >
          {isSearchLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Search'
          )}
        </Button>

        {searchQuery && (
          <Button
            sx={{ ml: 1 }}
            variant="outlined"
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        )}
      </Box>

      {/* DataGrid with correct ID handling */}
      <Box sx={{ width: '100%', mt: 2 }}>
        {clientsArray.length === 0 && !isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
            <Typography color="text.secondary">No clients found</Typography>
          </Box>
        ) : (
          <DataGrid
            rows={clientsArray}
            rowCount={clients?.total}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnSorting
            loading={isLoading}
            componentsProps={{
              noRowsOverlay: {
                style: {
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }
              }
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default Clients; 